import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, SelectField, RequirementsFields, cleanReqs } from "@/components/crm/fields";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const fmtINR = (v) => (v || v === 0) && v !== "" ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : null;

export function validateContact(f) {
  const e = {};
  if (!f.company?.trim()) e.company = "Company name is required";
  if (f.email && !EMAIL_RE.test(f.email)) e.email = "Enter a valid email";
  if (f.phone && f.phone.replace(/\D/g, "").length < 7) e.phone = "Enter a valid phone number";
  if (f.expected_yearly_turnover !== undefined && f.expected_yearly_turnover !== null && f.expected_yearly_turnover !== "" && !(Number(f.expected_yearly_turnover) >= 0)) e.expected_yearly_turnover = "Enter a valid amount";
  return e;
}

export function ContactFields({ form, set, errors = {}, showOwner, onBlurCheck }) {
  const { settings, activeUsers } = useMeta();
  const inp = (k, label, props = {}) => (
    <Field label={label} error={errors[k]}>
      <Input data-testid={`lead-${k}-input`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-9" {...props} />
    </Field>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">{inp("company", "Company name *", { placeholder: "e.g. Shreeji Chemicals Pvt Ltd", onBlur: onBlurCheck, autoFocus: true })}</div>
      {inp("contact_person", "Contact person")}
      {inp("designation", "Designation", { placeholder: "Purchase Manager" })}
      {inp("phone", "Phone", { inputMode: "tel", placeholder: "98XXXXXXXX", onBlur: onBlurCheck })}
      {inp("email", "Email", { type: "email", onBlur: onBlurCheck })}
      <Field label="Industry"><SelectField testid="lead-industry-select" value={form.industry} onChange={(v) => set("industry", v)} options={settings?.industries || []} /></Field>
      {inp("city", "City / location")}
      <Field label="EYT – Expected Yearly Turnover" hint="(₹)" error={errors.expected_yearly_turnover}>
        <Input data-testid="lead-eyt-input" type="number" min="0" inputMode="numeric" placeholder="e.g. 2500000" value={form.expected_yearly_turnover ?? ""} onChange={(e) => set("expected_yearly_turnover", e.target.value)} className="h-9" />
        {Number(form.expected_yearly_turnover) > 0 && <p className="text-[11px] text-gray-500">{fmtINR(form.expected_yearly_turnover)}</p>}
      </Field>
      <Field label="Lead source"><SelectField testid="lead-source-select" value={form.source} onChange={(v) => set("source", v)} options={settings?.sources || []} /></Field>
      <Field label="Priority"><SelectField testid="lead-priority-select" value={form.priority} onChange={(v) => set("priority", v)} options={["High", "Medium", "Low"]} allowNone={false} /></Field>
      {showOwner && (
        <Field label="Assigned employee" className="sm:col-span-2">
          <SelectField testid="lead-owner-select" value={form.owner_id} onChange={(v) => set("owner_id", v)} allowNone={false}
            options={activeUsers.map((u) => [u.user_id, `${u.name}${u.is_sample ? " (sample)" : ""}`])} />
        </Field>
      )}
    </div>
  );
}

export const DuplicateBox = ({ dups, onOpen }) => !dups?.length ? null : (
  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="duplicate-warning">
    <div className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle className="h-4 w-4" />Possible duplicate{dups.length > 1 ? "s" : ""} found</div>
    <ul className="mt-2 space-y-1">
      {dups.map((d) => (
        <li key={d.lead_id} className="text-amber-900 text-[13px]">
          <button type="button" className="underline-offset-2 hover:underline" onClick={() => onOpen?.(d)}>{d.company}</button>
          <span className="text-amber-700"> · {d.owner_name} · matched on {d.matched_on.join(", ")}</span>
        </li>
      ))}
    </ul>
  </div>
);

const blank = (uid) => ({ company: "", priority: "Medium", owner_id: uid });

export default function AddLeadDialog({ open, onOpenChange, onCreated }) {
  const { user, can } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(blank(user?.user_id));
  const [reqs, setReqs] = useState({});
  const [errors, setErrors] = useState({});
  const [dups, setDups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setStep(1); setForm(blank(user?.user_id)); setReqs({}); setErrors({}); setDups([]); } }, [open, user]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); if (["company", "email", "phone"].includes(k)) setDups([]); };

  const check = async () => {
    if (!form.company && !form.email && !form.phone) return;
    const r = await api.get("/leads/check-duplicates", { params: { company: form.company || "", email: form.email || "", phone: form.phone || "" } });
    setDups(r.data.duplicates);
  };

  const next = () => {
    const e = validateContact(form);
    setErrors(e);
    if (!Object.keys(e).length) setStep(2);
  };

  const save = async (force = false) => {
    const e = validateContact(form);
    setErrors(e);
    if (Object.keys(e).length) { setStep(1); return; }
    setSaving(true);
    try {
      const body = { ...form, requirements: cleanReqs(reqs) };
      if (!can("lead:assign")) delete body.owner_id;
      const r = await api.post("/leads", body, { params: { force } });
      toast.success(`${r.data.company} added`);
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (err) {
      if (err.response?.status === 409) {
        setDups(err.response.data.detail.duplicates);
        toast.warning("Possible duplicate — review before saving");
      } else toast.error(errMsg(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add lead</DialogTitle>
          <DialogDescription>Save after contact details, or add packaging requirements now.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 text-xs my-1">
          {["Contact details", "Packaging requirements"].map((s, i) => (
            <button key={s} type="button" data-testid={`add-lead-step-${i + 1}`} onClick={() => (i === 0 ? setStep(1) : next())}
              className={`flex items-center gap-2 ${step === i + 1 ? "text-[#0B3C2D] font-semibold" : "text-gray-400"}`}>
              <span className={`h-5 w-5 rounded-full grid place-items-center text-[11px] ${step > i + 1 ? "bg-[#0B3C2D] text-white" : step === i + 1 ? "bg-[#ECF5F1] text-[#0B3C2D] ring-1 ring-[#A3D9C9]" : "bg-gray-100"}`}>
                {step > i + 1 ? <Check className="h-3 w-3" /> : i + 1}
              </span>{s}
              {i === 0 && <span className="w-8 h-px bg-gray-200 ml-1" />}
            </button>
          ))}
        </div>
        <div className="space-y-4 mt-2">
          {step === 1 ? <ContactFields form={form} set={set} errors={errors} showOwner={can("lead:assign")} onBlurCheck={check} />
            : <RequirementsFields value={reqs} onChange={setReqs} compact />}
          <DuplicateBox dups={dups} onOpen={(d) => window.open(`/leads/${d.lead_id}`, "_blank")} />
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-4 border-t mt-2">
          <Button variant="ghost" onClick={() => (step === 2 ? setStep(1) : onOpenChange(false))} data-testid="add-lead-back">{step === 2 ? "Back" : "Cancel"}</Button>
          <div className="flex flex-col sm:flex-row gap-2">
            {dups.length > 0 && <Button variant="outline" disabled={saving} onClick={() => save(true)} data-testid="add-lead-save-anyway">Save anyway</Button>}
            {step === 1 && <Button variant="outline" disabled={saving} onClick={() => save(false)} data-testid="add-lead-save-step1">Save lead</Button>}
            {step === 1 ? <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" onClick={next} data-testid="add-lead-next">Next: Packaging</Button>
              : <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" disabled={saving} onClick={() => save(false)} data-testid="add-lead-submit">{saving ? "Saving…" : "Save lead"}</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
